import React, { useState, useEffect, useContext } from "react";
import { MachineContext } from "../fsm/useMachine";
import { string_id } from "../utils/getInfo";
import { XCircle } from "react-feather";

interface PrivacySettingsProps {
    close: () => void;
}

interface PrivacyData {
    title: string;
    description: string;
    alias: string;
    privacy: string;
    hasPassword: boolean;
    password?: string;
    passwordConfirm?: string;
    subGroupId?: string;
    multipleSubGroups?: string[];
}

interface SubGroup {
    sub_group_id: string;
    metadata: {
        name: string;
    };
}

interface PrivacyResponse {
    title: string;
    description: string;
    alias: string;
    privacy: string;
    has_password: boolean;
    allowed_privacies: string[];
    sub_groups?: SubGroup[];
    enterprise_sub_group_id?: string;
    enterprise_sub_group_ids?: string[];
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({ close }) => {
    const [current] = useContext(MachineContext);
    const { flow } = current.context;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");
    const [saving, setSaving] = useState(false);

    const [privacyData, setPrivacyData] = useState<PrivacyData>(() => ({
        title: flow?.title || "",
        description: "",
        alias: "",
        privacy: "protected",
        hasPassword: false,
        password: "",
        passwordConfirm: "",
        subGroupId: undefined,
        multipleSubGroups: []
    }));

    const [allowedPrivacies, setAllowedPrivacies] = useState<string[]>([]);
    const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
    const [showPasswordFields, setShowPasswordFields] = useState(false);

    // Fetch current privacy settings
    useEffect(() => {
        const fetchPrivacySettings = async () => {
            try {
                const response = await fetch(`/flow/${string_id}/privacy_data`);
                if (!response.ok) {
                    throw new Error("Failed to fetch privacy settings");
                }
                const data: PrivacyResponse = await response.json();

                setPrivacyData({
                    title: data.title || flow.title || "",
                    description: data.description || "",
                    alias: data.alias || "",
                    privacy: data.privacy || "protected",
                    hasPassword: data.has_password || false,
                    password: "",
                    passwordConfirm: "",
                    subGroupId: data.enterprise_sub_group_id,
                    multipleSubGroups: data.enterprise_sub_group_ids || []
                });

                // Ensure allowed_privacies is an array
                const privacies = Array.isArray(data.allowed_privacies)
                    ? data.allowed_privacies
                    : ["protected", "private", "public"];
                setAllowedPrivacies(privacies);

                // Ensure sub_groups is an array
                const groups = Array.isArray(data.sub_groups) ? data.sub_groups : [];
                setSubGroups(groups);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching privacy settings:", err);
                setError("Failed to load privacy settings");
                setLoading(false);
            }
        };

        fetchPrivacySettings();
    }, []);

    const getPrivacyLabel = (privacy: string): string => {
        switch (privacy) {
            case "groups":
            case "enterprise_private":
                return "Group Private";
            case "private":
                return "Only I can see it (Private)";
            case "public":
                return "Anyone can see it (Public)";
            case "protected":
                return "Need a link to see (Protected)";
            default:
                return privacy;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setStatus("");

        // Validation
        if (privacyData.hasPassword) {
            if (!privacyData.password) {
                setError("Password is required when password protection is enabled");
                return;
            }
            if (privacyData.password !== privacyData.passwordConfirm) {
                setError("Passwords do not match");
                return;
            }
        }

        setSaving(true);

        try {
            const formData = new FormData();
            formData.append("string_id", string_id);
            formData.append("title", privacyData.title);
            formData.append("description", privacyData.description);
            formData.append("alias", privacyData.alias);
            formData.append("privacy", privacyData.privacy);

            if (privacyData.hasPassword) {
                formData.append("has_password", "true");
                formData.append("password_protect", "protected");
                if (privacyData.password) {
                    formData.append("flow_password", privacyData.password);
                    formData.append("flow_password_confirm", privacyData.passwordConfirm || "");
                }
            }

            if (privacyData.privacy === "groups" && privacyData.multipleSubGroups && privacyData.multipleSubGroups.length > 0) {
                privacyData.multipleSubGroups.forEach(groupId => {
                    formData.append("groups[]", groupId);
                });
            }

            const response = await fetch(`/flow/${string_id}/update`, {
                method: "POST",
                body: formData
            });

            if (!response.ok) {
                throw new Error("Failed to save privacy settings");
            }

            setStatus("Privacy settings saved successfully!");
            setTimeout(() => {
                close();
            }, 1500);
        } catch (err) {
            console.error("Error saving privacy settings:", err);
            setError("Failed to save privacy settings. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handlePrivacyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;

        // Handle group selection with ID
        if (value.startsWith("groups:")) {
            const groupId = value.split(":")[1];
            setPrivacyData(prev => ({
                ...prev,
                privacy: "groups",
                subGroupId: groupId,
                multipleSubGroups: [groupId]
            }));
        } else {
            setPrivacyData(prev => ({
                ...prev,
                privacy: value,
                subGroupId: undefined
            }));
        }
    };

    const handleSubGroupCheckbox = (groupId: string, checked: boolean) => {
        setPrivacyData(prev => {
            const current = prev.multipleSubGroups || [];
            const updated = checked
                ? [...current, groupId]
                : current.filter(id => id !== groupId);
            return {
                ...prev,
                multipleSubGroups: updated
            };
        });
    };

    if (loading) {
        return (
            <div className="settings-modal-2025">
                <div className="settings-header">
                    <h2>Privacy & Sharing</h2>
                    <XCircle className="feather close-icon" onClick={close} style={{ cursor: 'pointer' }} />
                </div>
                <div className="settings-content" style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Loading privacy settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-modal-2025">
            <div className="settings-header">
                <h2>Privacy & Sharing Settings</h2>
                <XCircle className="feather close-icon" onClick={close} style={{ cursor: 'pointer' }} />
            </div>

            <form onSubmit={handleSubmit}>
                <div className="settings-content">
                    {error && (
                        <div className="privacy-error" style={{
                            background: '#fee2e2',
                            border: '1px solid #fecaca',
                            color: '#991b1b',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    {status && (
                        <div className="privacy-status" style={{
                            background: '#dbeafe',
                            border: '1px solid #bfdbfe',
                            color: '#1e40af',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {status}
                        </div>
                    )}

                    <section className="settings-section">
                        <div className="settings-card">
                            {/* Title */}
                            <div className="setting-group">
                                <label className="setting-label-block">
                                    Title
                                </label>
                                <input
                                    type="text"
                                    className="setting-input-full"
                                    value={privacyData.title}
                                    onChange={(e) => setPrivacyData(prev => ({ ...prev, title: e.target.value }))}
                                />
                            </div>

                            {/* Description */}
                            <div className="setting-group">
                                <label className="setting-label-block">
                                    Description
                                </label>
                                <textarea
                                    className="settings-textarea"
                                    value={privacyData.description}
                                    onChange={(e) => setPrivacyData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Add a description for your Flow..."
                                    rows={3}
                                />
                            </div>

                            {/* Custom URL */}
                            <div className="setting-group">
                                <label className="setting-label-block">
                                    Custom URL
                                </label>
                                <div className="url-input-group">
                                    <span className="url-prefix">
                                        http://flowvel.la/
                                    </span>
                                    <input
                                        type="text"
                                        className="url-input"
                                        value={privacyData.alias}
                                        onChange={(e) => setPrivacyData(prev => ({ ...prev, alias: e.target.value }))}
                                    />
                                </div>
                            </div>

                            {/* Privacy Setting */}
                            <div className="setting-group">
                                <label className="setting-label-block">
                                    Who can view this Flow?
                                </label>
                                <select
                                    className="setting-select-full"
                                    value={privacyData.privacy === "groups" && privacyData.subGroupId ? `groups:${privacyData.subGroupId}` : privacyData.privacy}
                                    onChange={handlePrivacyChange}
                                >
                                    {allowedPrivacies.map((privacy) => {
                                        const label = getPrivacyLabel(privacy);
                                        return (
                                            <option key={privacy} value={privacy}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                    {subGroups.map((group) => (
                                        <option key={group.sub_group_id} value={`groups:${group.sub_group_id}`}>
                                            {group.metadata.name}
                                        </option>
                                    ))}
                                </select>

                                {/* Multiple subgroups selection */}
                                {privacyData.privacy === "groups" && subGroups.length > 0 && (
                                    <div className="subgroups-container">
                                        <label className="setting-label-block">
                                            Select Groups
                                        </label>
                                        {subGroups.map((group) => (
                                            <div key={group.sub_group_id} className="checkbox-row">
                                                <label className="setting-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={privacyData.multipleSubGroups?.includes(group.sub_group_id) || false}
                                                        onChange={(e) => handleSubGroupCheckbox(group.sub_group_id, e.target.checked)}
                                                    />
                                                    <span>{group.metadata.name}</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Password Protection (only show for protected flows) */}
                            {privacyData.privacy === "protected" && (
                                <div className="password-container">
                                    <label className="setting-label">
                                        <input
                                            type="checkbox"
                                            checked={privacyData.hasPassword}
                                            onChange={(e) => {
                                                setPrivacyData(prev => ({ ...prev, hasPassword: e.target.checked }));
                                                setShowPasswordFields(e.target.checked);
                                            }}
                                        />
                                        <span>Password Protect Flow</span>
                                    </label>

                                    {privacyData.hasPassword && showPasswordFields && (
                                        <div className="password-fields">
                                            <input
                                                type="password"
                                                className="setting-input-full"
                                                value={privacyData.password || ""}
                                                onChange={(e) => setPrivacyData(prev => ({ ...prev, password: e.target.value }))}
                                                placeholder="Password"
                                            />
                                            <input
                                                type="password"
                                                className="setting-input-full"
                                                value={privacyData.passwordConfirm || ""}
                                                onChange={(e) => setPrivacyData(prev => ({ ...prev, passwordConfirm: e.target.value }))}
                                                placeholder="Confirm password"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                <div className="settings-footer">
                    <button
                        type="button"
                        onClick={close}
                        className="nav-button"
                        style={{ marginRight: '12px' }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="done-button"
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Privacy Settings"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PrivacySettings;
